import api from '@/shared/api/api';
import { uploadFileToSignedUrl } from '@/shared/lib/uploadSignedUrl';
import type {
  CreatePackPayload,
  GetPacksParams,
  IPack,
  Pack,
  PaginatedPacksResponse,
} from '../types';
import type { PackPreset } from '../types/preset.types';

export interface SignedUploadResponse {
  signedUploadUrl: string;
  path: string;
  token: string;
  publicUrl: string;
}

export const packApi = {
  list: async (
    params?: GetPacksParams,
    signal?: AbortSignal
  ): Promise<PaginatedPacksResponse> => {
    const mapped: Record<string, unknown> = {};
    const p = params ?? {};
    if (p.page !== undefined) mapped.page = p.page;
    if (p.limit !== undefined) mapped.limit = p.limit;
    if (p.name !== undefined) mapped.name = p.name;
    if (p.creator !== undefined) mapped.creator = p.creator;
    if (p.published !== undefined) mapped.published = p.published;
    if (p.sort !== undefined) mapped.sort = p.sort;
    return api<PaginatedPacksResponse>({
      url: '/v1/packs',
      method: 'GET',
      params: mapped,
      signal,
    });
  },

  get: async (id: string, signal?: AbortSignal): Promise<Pack> => {
    return api<Pack>({
      url: `/v1/packs/${id}`,
      method: 'GET',
      signal,
    });
  },

  getPresets: async (
    packId: string,
    signal?: AbortSignal
  ): Promise<PackPreset[]> => {
    return api<PackPreset[]>({
      url: `/v1/packs/${packId}/presets`,
      method: 'GET',
      signal,
    });
  },

  create: async (data: CreatePackPayload): Promise<IPack> => {
    return api<IPack>({
      url: '/v1/packs',
      method: 'POST',
      data: data,
    });
  },

  update: async (id: string, data: CreatePackPayload): Promise<IPack> => {
    return api<IPack>({
      url: `/v1/packs?id=${id}`,
      method: 'POST',
      data: data,
    });
  },

  /** Obtém URL assinada para upload do cover. Depois use uploadFileToSignedUrl. */
  getCoverUploadUrl: async (id: string): Promise<SignedUploadResponse> => {
    return api<SignedUploadResponse>({
      url: `/v1/packs/${id}/cover`,
      method: 'PATCH',
    });
  },

  /** Upload do cover via URL assinada (chama getCoverUploadUrl + PUT para Supabase). */
  uploadCover: async (packId: string, file: File): Promise<string> => {
    const { signedUploadUrl, token, publicUrl } = await packApi.getCoverUploadUrl(packId);
    await uploadFileToSignedUrl(signedUploadUrl, token, file);
    return publicUrl;
  },

  remove: async (id: string): Promise<void> => {
    await api({ url: `/v1/packs/${id}`, method: 'DELETE' });
  },
};
